/**
 * Floating Slack quick-post dock — sits next to the Aircall bubble at the
 * bottom-right of every page.
 *
 * Aircall pill: bottom: 24, right: 24, size 56x56 (z=9999)
 * Slack pill : bottom: 24, right: 96 (= 24 + 56 + 16 gap), size 56x56
 *
 * When expanded, a 360x420 slide-out panel pops above the pill with a
 * channel picker and a multi-line composer. Send POSTs to /api/slack/post
 * via the existing apiClient (token + admin auth headers handled there).
 */
import { useState, useMemo, useEffect, useRef } from "react";
import { Send, ChevronDown, Hash, Lock, Loader2, RefreshCw, AlertCircle, GripVertical } from "lucide-react";
import { useSlack } from "@/contexts/SlackContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { slackPost } from "@/lib/api";
import { SlackMark } from "@/components/BrandMarks";

const DEFAULT_POS = { right: 96, bottom: 24 };

export default function SlackDock() {
  const ctx = useSlack();
  const [channelId, setChannelId] = useState("");
  const [text, setText]           = useState("");
  const [sending, setSending]     = useState(false);
  const [pos, setPos] = useState(() => {
    try { return JSON.parse(localStorage.getItem("slack_dock_pos")) || DEFAULT_POS; }
    catch { return DEFAULT_POS; }
  });
  const dragOffset = useRef(null);

  const onDragStart = (e) => {
    dragOffset.current = { startX: e.clientX, startY: e.clientY, startRight: pos.right, startBottom: pos.bottom };
    const move = (ev) => {
      const dx = ev.clientX - dragOffset.current.startX;
      const dy = ev.clientY - dragOffset.current.startY;
      setPos({
        right:  Math.max(8, dragOffset.current.startRight  - dx),
        bottom: Math.max(8, dragOffset.current.startBottom - dy),
      });
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      dragOffset.current = null;
      setPos((p) => { localStorage.setItem("slack_dock_pos", JSON.stringify(p)); return p; });
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  // Consume a prefilled draft (email snap) when the dock is opened with one.
  const draft = ctx?.draft;
  useEffect(() => {
    if (draft) {
      setText(draft);
      ctx.clearDraft();
    }
  }, [draft]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Channel picker grouped: starred/recent (none for now), then alpha list.
  // Computed before the early-return so the hook order stays stable.
  const channels = ctx?.channels;
  const channelOptions = useMemo(() => {
    return (channels || []).map(c => ({
      value: c.id,
      label: `${c.is_private ? "🔒" : "#"}${c.name}`,
      isPrivate: c.is_private,
    }));
  }, [channels]);

  if (!ctx) return null;
  const { expanded, expand, collapse, status, loadingChannels, channelsError, reloadChannels } = ctx;

  const isConfigured = status?.configured;
  const isHealthy    = status?.ok;
  const teamName     = status?.team;
  const defaultCh    = status?.default_channel;

  // Auto-select the default channel the first time we have one and
  // the user hasn't picked anything yet.
  const effectiveChannel = channelId || defaultCh || "";

  const handleSend = async () => {
    if (!effectiveChannel) {
      toast.error("Pick a channel first");
      return;
    }
    if (!text.trim()) {
      toast.error("Type a message first");
      return;
    }
    setSending(true);
    try {
      await slackPost({
        channel: effectiveChannel,
        text:    text.trim(),
        actor:   "Ops",
        purpose: "quick_post",
      });
      toast.success(`Posted to ${effectiveChannel.startsWith("C") || effectiveChannel.startsWith("G") ? "channel" : effectiveChannel}`);
      setText("");
    } catch (e) {
      toast.error("Slack post failed: " + (e?.response?.data?.detail || e?.message || ""));
    } finally {
      setSending(false);
    }
  };

  // ── Collapsed: nothing floats — reopened from the sidebar launcher ──
  if (!expanded) {
    return null;
  }

  // ── Expanded: composer panel (drag via the header grip) ──────────────
  return (
    <div
      data-testid="slack-dock-panel"
      style={{ position: "fixed", right: pos.right, bottom: pos.bottom, width: 360, height: 420, zIndex: 9999 }}
      className="bg-white rounded-xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div
        className="bg-slate-900 text-white px-3 py-2 flex items-center gap-2 cursor-move select-none"
        onMouseDown={onDragStart}
        data-testid="slack-dock-header"
      >
        <GripVertical className="w-4 h-4 text-slate-400" />
        <SlackMark className="w-4 h-4" />
        <span className="text-sm font-medium">Slack</span>
        {isHealthy ? (
          <Badge variant="outline" className="text-[10px] h-5 bg-emerald-500/20 text-emerald-100 border-emerald-500/40">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 mr-1.5" />
            {teamName || "online"}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] h-5 bg-amber-500/20 text-amber-100 border-amber-500/40">
            not configured
          </Badge>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={collapse}
          className="h-6 w-6 ml-auto text-slate-300 hover:text-white hover:bg-slate-800"
          title="Collapse"
          data-testid="slack-dock-collapse"
        >
          <ChevronDown className="w-4 h-4" />
        </Button>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col p-3 gap-3 overflow-hidden">
        {!isConfigured ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-2 px-4">
            <AlertCircle className="w-8 h-8 text-amber-500" />
            <div className="text-sm font-medium text-slate-700">Slack isn&apos;t connected yet</div>
            <div className="text-xs text-slate-500 leading-relaxed">
              An admin needs to add the Slack bot token in <span className="font-medium text-slate-700">Admin → API Credentials → Slack</span>.
            </div>
            <a
              href="/admin/settings"
              className="text-xs text-blue-600 hover:underline mt-1"
              data-testid="slack-dock-settings-link"
            >
              Open Admin Settings →
            </a>
          </div>
        ) : (
          <>
            {/* Channel picker */}
            <div className="flex items-center gap-1.5">
              <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                Channel
              </label>
              <button
                type="button"
                onClick={reloadChannels}
                className="ml-auto text-slate-400 hover:text-slate-600"
                title="Reload channel list"
                data-testid="slack-dock-reload-channels"
                disabled={loadingChannels}
              >
                {loadingChannels
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <RefreshCw className="w-3 h-3" />}
              </button>
            </div>
            <div className="relative">
              <select
                value={effectiveChannel}
                onChange={(e) => setChannelId(e.target.value)}
                className="w-full text-sm border rounded-md px-2 py-1.5 bg-white pr-7 appearance-none cursor-pointer"
                disabled={loadingChannels}
                data-testid="slack-dock-channel"
              >
                <option value="">— Pick a channel —</option>
                {channelOptions.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {effectiveChannel && (
                effectiveChannel === defaultCh ? (
                  <Hash className="w-3 h-3 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                ) : null
              )}
            </div>
            {channelsError && (
              <div className="text-[11px] text-rose-600">
                {channelsError}
              </div>
            )}

            {/* Composer */}
            <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
              Message
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type a quick update for the team…"
              rows={5}
              className="flex-1 text-sm border rounded-md px-2 py-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              data-testid="slack-dock-text"
              maxLength={3000}
            />
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground tabular-nums">
                {text.length}/3000
              </span>
              <Button
                onClick={handleSend}
                disabled={sending || !text.trim() || !effectiveChannel}
                size="sm"
                className="h-8 px-3"
                data-testid="slack-dock-send"
              >
                {sending
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                  : <Send className="w-3.5 h-3.5 mr-1" />}
                Send
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
